/**
 * <sticky-add-to-cart>
 *
 * Bottom-of-viewport sticky bar that:
 *   - Appears once the main .product-form__submit scrolls out of view
 *   - Hides as soon as it scrolls back in
 *   - Adds the CURRENTLY SELECTED variant — initial value is
 *     product.selected_or_first_available_variant, but updates live when
 *     the customer changes the main variant picker (subscribes to
 *     PUB_SUB_EVENTS.variantChange that the Craft variant-selects fires).
 *
 * Architecture mirrors cart-recommendations.js: AJAX POST to /cart/add.js,
 * then cartDrawer.renderContents(response) re-renders the drawer so the
 * user sees their item drop in.
 */

class StickyAddToCart extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.targetSelector = this.dataset.targetSelector || '.product-form__submit';
    this.target = document.querySelector(this.targetSelector);

    // No main add-to-cart on this page (e.g. wholesale-only product) — bail.
    if (!this.target) return;

    this.observer = new IntersectionObserver(this.onIntersection.bind(this), {
      // Negative bottom margin so the bar doesn't toggle while the main
      // button is partially clipped at the very bottom — feels less jittery.
      rootMargin: '0px 0px -40px 0px',
    });
    this.observer.observe(this.target);

    this.addEventListener('submit', this.onSubmit.bind(this));

    // Subscribe to Craft's variantChange pub/sub so the bar's variant ID,
    // price, and button state stay in sync with the on-page picker.
    if (
      typeof subscribe === 'function' &&
      typeof PUB_SUB_EVENTS !== 'undefined' &&
      PUB_SUB_EVENTS.variantChange
    ) {
      this.variantChangeUnsubscriber = subscribe(
        PUB_SUB_EVENTS.variantChange,
        this.onVariantChange.bind(this)
      );
    }
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
  }

  onIntersection(entries) {
    const mainButtonVisible = entries[0].isIntersecting;
    if (mainButtonVisible) {
      this.setAttribute('hidden', '');
    } else {
      this.removeAttribute('hidden');
    }
  }

  /**
   * Craft fires variantChange with { data: { variant, html, ... } }.
   *   - variant: the matching variant object, or null when the selected
   *     option combo doesn't map to a real variant
   *   - html: a Document of the freshly-fetched product page, with the
   *     new variant already rendered (use this to copy the formatted price)
   */
  onVariantChange(event) {
    if (!event || !event.data) return;

    const variant = event.data.variant || null;
    const html = event.data.html || null;

    this.updateForVariant(variant, html);
  }

  updateForVariant(variant, html) {
    const idInput = this.querySelector('[data-sticky-atc-variant-id]');
    const priceEl = this.querySelector('[data-sticky-atc-price]');
    const button = this.querySelector('[data-sticky-atc-button]');
    const labelEl = this.querySelector('[data-sticky-atc-button-label]');
    if (!button || !labelEl) return;

    const isAvailable = !!(variant && variant.available);
    const isMatchedVariant = !!variant;

    // 1. Hidden variant ID — keep whatever Shopify says is current.
    //    If variant is null (no matching variant), leave the previous value
    //    in place; the form is disabled anyway.
    if (idInput && variant && variant.id) {
      idInput.value = variant.id;
    }

    // 2. Price — pull the freshly-formatted price from the new product HTML.
    //    Falls back silently if we can't find it (e.g. theme markup changed).
    if (priceEl && html && variant) {
      const sourcePrice =
        html.querySelector('[id^="MainProduct"] .price-item--regular') ||
        html.querySelector('.price-item--regular') ||
        html.querySelector('.price-item--sale');
      if (sourcePrice) {
        priceEl.textContent = sourcePrice.textContent.trim();
      }
    }

    // 3. Button state — toggle between Add to cart / Sold out / Unavailable
    if (isAvailable) {
      button.classList.remove('sticky-atc__add--sold-out');
      button.removeAttribute('disabled');
      button.removeAttribute('aria-disabled');
      labelEl.textContent = button.dataset.labelAdd || 'Add to cart';
    } else {
      button.classList.add('sticky-atc__add--sold-out');
      button.setAttribute('disabled', 'disabled');
      button.setAttribute('aria-disabled', 'true');
      labelEl.textContent = isMatchedVariant
        ? button.dataset.labelSoldOut || 'Sold out'
        : button.dataset.labelUnavailable || 'Unavailable';
    }
  }

  onSubmit(event) {
    const form = event.target.closest('[data-sticky-atc-form]');
    if (!form) return;
    event.preventDefault();

    const button = form.querySelector('[data-sticky-atc-button]');
    if (
      !button ||
      button.disabled ||
      button.getAttribute('aria-disabled') === 'true'
    ) {
      return;
    }

    button.setAttribute('aria-disabled', 'true');
    button.classList.add('is-loading');

    const cart =
      document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    const formData = new FormData(form);

    if (cart) {
      formData.append(
        'sections',
        cart.getSectionsToRender().map((section) => section.id).join(',')
      );
      formData.append('sections_url', window.location.pathname);
      cart.setActiveElement && cart.setActiveElement(document.activeElement);
    }

    const config =
      typeof fetchConfig === 'function'
        ? fetchConfig('javascript')
        : {
            method: 'POST',
            headers: {
              Accept: 'application/javascript',
              'X-Requested-With': 'XMLHttpRequest',
            },
          };
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];
    config.body = formData;

    fetch(window.routes ? window.routes.cart_add_url : '/cart/add.js', config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          console.warn('[sticky-add-to-cart] add rejected', response);
          return;
        }
        if (cart && typeof cart.renderContents === 'function') {
          // Strip 'is-empty' off the wrapper before renderContents — the
          // theme's open() handler reads this class to pick which container
          // to trap focus in, and an empty -> non-empty add leaves the
          // stale class behind (theme bug), causing trapFocus(null, ...).
          if (cart.classList.contains('is-empty')) {
            cart.classList.remove('is-empty');
          }
          cart.renderContents(response);
        } else {
          window.location = (window.routes && window.routes.cart_url) || '/cart';
        }

        if (
          typeof publish === 'function' &&
          typeof PUB_SUB_EVENTS !== 'undefined' &&
          PUB_SUB_EVENTS.cartUpdate
        ) {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'sticky-add-to-cart',
            productVariantId: formData.get('id'),
            cartData: response,
          });
        }
      })
      .catch((e) => console.error('[sticky-add-to-cart]', e))
      .finally(() => {
        button.removeAttribute('aria-disabled');
        button.classList.remove('is-loading');
      });
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCart);
}
